import requestApis from "apis/request";
import { commonConstants } from "constants/index";
import {
  IBffSeller,
  IBffTerminal,
  IGetSellerListParams,
  IGetSellerListResponse,
  IGetSellerOrEmptyParams,
  IGetUserTerminalsParams,
  IPaginationQueryParams,
  IPlatform,
  ISeller,
  Terminal,
  GetListPlatformResponse,
  GetListPlatformParams,
} from "models";
import { getData, getResult } from "utils/request";

const { staffBffClient } = requestApis;
const { sellerClient } = requestApis;
const { DEFAULT_FILTERS, X_SELLER_ID } = commonConstants;

const getSellersOrEmpty = async (params?: IGetSellerOrEmptyParams): Promise<Array<ISeller>> => {
  const ret = await sellerClient.get("/sellers/get-all", { params });
  return (ret?.data?.result?.sellers || []) as Array<ISeller>;
};

const getTerminals = async (params: { sellerId: ISeller["id"] }): Promise<Terminal[]> => {
  const ret = await sellerClient.get(`terminals?pageSize=200&page=1&sellerID=${params.sellerId}`);
  return ret.data.result.terminals;
};

const getSellers = async (): Promise<IBffSeller[]> => {
  try {
    const ret = await staffBffClient.get("/api/v1/sellers");
    return ret.data.data;
  } catch {
    return [];
  }
};

const getSellersByPlatform = async (platformId: IPlatform["id"]): Promise<IBffSeller[]> => {
  try {
    const ret = await staffBffClient.get(`api/v1/platforms/${platformId}/sellers`);
    return ret.data.result.sellers;
  } catch {
    return [];
  }
};

const getTerminalsByPlatform = async (platformCode?: IPlatform["code"], sellerId?: number): Promise<IBffTerminal[]> => {
  try {
    const terminals: IBffTerminal[] = [];
    // Get all terminals by checking response list has length smaller than the page size
    let respLength = DEFAULT_FILTERS.TERMINAL.pageSize;
    // Limit the amount of call to 100 times
    for (let i = 1; i <= 100; i++) {
      const resp = await staffBffClient.get(`/api/v1/terminals`, {
        params: {
          page: i,
          pageSize: DEFAULT_FILTERS.TERMINAL.pageSize,
          sellingPlatformCodes: [platformCode],
          sellerId,
        },
      });
      respLength = resp.data?.result?.terminals?.length;
      terminals.push(...resp.data?.result?.terminals);
      if (!respLength || respLength < DEFAULT_FILTERS.TERMINAL.pageSize) {
        break;
      }
    }
    return terminals;
  } catch {
    return [];
  }
};

const getTerminalsBySeller = async (
  sellerId: ISeller["id"],
  params?: IPaginationQueryParams
): Promise<IBffTerminal[]> => {
  try {
    const ret = await staffBffClient.get(`/api/v1/sellers/${sellerId}/terminals`, { params });
    return ret.data.data;
  } catch {
    return [];
  }
};

const getPlatformOptionsOrEmpty = async (
  params: { page: number; pageSize: number; ownerSellerId?: ISeller["id"] } = DEFAULT_FILTERS.PLATFORM
): Promise<IPlatform[]> => {
  try {
    const ret = await staffBffClient.get(`/api/v1/platforms`, {
      params,
    });
    const { platforms } = ret.data.result;
    return platforms || [];
  } catch {
    return [];
  }
};

const getPlatform = async (platformId: IPlatform["id"]): Promise<IPlatform | undefined> => {
  try {
    const ret = await staffBffClient.get(`/api/v1/platforms`, {
      params: { platformId },
    });
    const { platforms } = ret.data.result;
    return platforms && platforms[0];
  } catch {
    return undefined;
  }
};

const getSellingPlatforms = async (sellerId: ISeller["id"]): Promise<IPlatform[]> => {
  try {
    const res = await sellerClient.get(`/sellers/${sellerId}/platforms`);
    return res.data.result?.platforms || [];
  } catch {
    return [];
  }
};

const getAllPlatforms = async (): Promise<IPlatform[]> => {
  const MAX_LOOP_COUNT = 10;
  const MAX_PAGE_SIZE = 200;

  let currentPage = 1;
  let allPlatforms: IPlatform[] = [];

  let loopCount = 0; // hardcode to prevent loop too many times

  while (true) {
    if (loopCount >= MAX_LOOP_COUNT) {
      break;
    }

    const response = await sellerClient.get("/platforms", {
      params: {
        pageSize: MAX_PAGE_SIZE,
        page: currentPage,
      },
    });

    const result = response.data.result;

    allPlatforms = allPlatforms.concat(result.platforms);

    if (result.platforms.length < MAX_PAGE_SIZE) {
      break;
    }

    currentPage++;
    loopCount++;
  }

  return allPlatforms;
};

const getAllTerminalsBySeller = async (sellerId: number): Promise<IBffTerminal[]> => {
  const MAX_LOOP_COUNT = 10;
  const MAX_PAGE_SIZE = 100;

  let currentPage = 1;
  let allTerminals: IBffTerminal[] = [];

  let loopCount = 0;

  while (true) {
    if (loopCount >= MAX_LOOP_COUNT) {
      break;
    }

    const data = await getTerminalsBySeller(sellerId, {
      pageSize: MAX_PAGE_SIZE,
      page: currentPage,
    });

    allTerminals = allTerminals.concat(data);

    if (data.length === 0 || data.length < MAX_PAGE_SIZE) {
      break;
    }

    currentPage++;
    loopCount++;
  }

  return allTerminals;
};

const getUserTerminals = async (params: IGetUserTerminalsParams): Promise<IBffTerminal[]> => {
  try {
    const res = await staffBffClient.get("/api/v1/terminals/user", { params });
    return res.data.terminals;
  } catch {
    return [];
  }
};

const getSellerList = (params: IGetSellerListParams, sellerId?: string): Promise<IGetSellerListResponse> => {
  return staffBffClient
    .get("/api/v1/seller-list", {
      params,
      headers: {
        [X_SELLER_ID]: sellerId,
      },
    })
    .then(getData);
};

const getListPlatforms = async (params: GetListPlatformParams): Promise<GetListPlatformResponse> => {
  return staffBffClient
    .get("/api/v1/platforms", {
      params,
    })
    .then(getResult);
};

export default {
  getSellers,
  getSellersByPlatform,
  getTerminalsByPlatform,
  getTerminalsBySeller,
  getAllTerminalsBySeller,
  getSellersOrEmpty,
  getTerminals,
  getPlatformOptionsOrEmpty,
  getPlatform,
  getSellingPlatforms,
  getAllPlatforms,
  getUserTerminals,
  getSellerList,
  getListPlatforms,
};
